import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle, BookOpen, LibraryBig } from "lucide-react";

const CheckoutSuccess = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | failed | timeout
  const [result, setResult] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { navigate("/"); return; }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      if (attempts.current >= 6) { setState("timeout"); return; }
      attempts.current += 1;
      try {
        const res = await api.get(`/checkout/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setResult(res.data);
          await checkAuth();
          setState("paid");
          return;
        }
        if (res.data.status === "expired") { setState("failed"); return; }
        setTimeout(poll, 2000);
      } catch (e) {
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, navigate, checkAuth]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9F9F7] px-6">
      <div className="max-w-md w-full border border-stone-200 bg-white p-10 text-center" data-testid="checkout-status">
        {state === "checking" && (
          <>
            <Loader2 className="w-12 h-12 animate-spin text-[#9A3412] mx-auto mb-6" />
            <h1 className="font-serif text-3xl text-stone-900">Confirming your payment…</h1>
            <p className="text-stone-500 mt-2">This only takes a moment.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="w-14 h-14 text-[#166534] mx-auto mb-6" />
            <h1 className="font-serif text-4xl text-stone-900">You're all set!</h1>
            <p className="text-stone-600 mt-3">
              {result?.product_type === "bundle" ? "All 9 guides are now in your library." : "Your guide has been unlocked and added to your library."}
            </p>
            <div className="mt-8 flex flex-col gap-3">
              {result?.product_type === "guide" ? (
                <Button asChild className="bg-[#9A3412] hover:bg-[#7C2D12] text-white rounded-none py-6"><Link to={`/read/${result.slug}`} data-testid="success-read-btn"><BookOpen className="w-4 h-4 mr-2" />Read now</Link></Button>
              ) : (
                <Button asChild className="bg-[#9A3412] hover:bg-[#7C2D12] text-white rounded-none py-6"><Link to="/library" data-testid="success-library-btn"><LibraryBig className="w-4 h-4 mr-2" />Go to My Library</Link></Button>
              )}
              <Button asChild variant="outline" className="rounded-none"><Link to="/library">View My Library</Link></Button>
            </div>
          </>
        )}
        {(state === "failed" || state === "timeout") && (
          <>
            <XCircle className="w-14 h-14 text-red-500 mx-auto mb-6" />
            <h1 className="font-serif text-3xl text-stone-900">{state === "timeout" ? "Still processing" : "Payment not completed"}</h1>
            <p className="text-stone-600 mt-3">{state === "timeout" ? "Your payment is taking longer than usual. Check your library shortly or contact support." : "The payment was cancelled or failed. You have not been charged."}</p>
            <Button asChild className="mt-8 bg-[#9A3412] hover:bg-[#7C2D12] text-white rounded-none py-6"><Link to="/">Back to guides</Link></Button>
          </>
        )}
      </div>
    </div>
  );
};

export default CheckoutSuccess;
